import { Button, FormControl, HorizontalStack, Input, Modal, Text, Textarea } from '@lego/klik-ui';
import { type ChangeEvent, useEffect, useState } from 'react';
import { z } from 'zod';
import { getApiErrorMessage } from '@/hooks/api';
import { useCreateWorkspace } from '@/hooks/workspace';

const NAME_MIN_LENGTH = 5;
const NAME_MAX_LENGTH = 42;
const DESCRIPTION_MAX_LENGTH = 2000;

const nameSchema = z
  .string()
  .min(NAME_MIN_LENGTH)
  .max(NAME_MAX_LENGTH)
  .regex(/^[a-z0-9._]+$/);
const productIdSchema = z.string().regex(/^\d{3,10}$/);
const emailSchema = z.string().email().max(255);

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: () => void;
};

export const CreateWorkspaceDialog = ({ isOpen, onClose, onCreated }: Props) => {
  const [name, setName] = useState('');
  const [productId, setProductId] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const createWorkspace = useCreateWorkspace();

  useEffect(() => {
    if (isOpen) {
      setName('');
      setProductId('');
      setOwnerEmail('');
      setDescription('');
      setError(undefined);
    }
  }, [isOpen]);

  const trimmedName = name.trim();
  const nameHasInvalidChars =
    trimmedName.length > 0 &&
    !nameSchema.safeParse(trimmedName).success &&
    trimmedName.length >= NAME_MIN_LENGTH;
  const nameIsTooShort = trimmedName.length > 0 && trimmedName.length < NAME_MIN_LENGTH;
  const productIdIsInvalid = productId.length > 0 && !productIdSchema.safeParse(productId).success;
  const emailIsInvalid = ownerEmail.length > 0 && !emailSchema.safeParse(ownerEmail.trim()).success;

  const canSubmit =
    nameSchema.safeParse(trimmedName).success &&
    productIdSchema.safeParse(productId).success &&
    emailSchema.safeParse(ownerEmail.trim()).success &&
    !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    setError(undefined);
    try {
      await createWorkspace({
        name: trimmedName,
        enterpriseArchitectureProductId: `PRO-${productId}`,
        ownerEmail: ownerEmail.trim(),
        description: description.trim() || null,
      });
      onCreated?.();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to create workspace'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>Create Workspace</Modal.Header>
        <Modal.CloseButton />
        <Modal.Body>
          <FormControl isInvalid={nameIsTooShort || nameHasInvalidChars} isRequired>
            <FormControl.Label>Name</FormControl.Label>
            <Input
              maxLength={NAME_MAX_LENGTH}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  void handleSubmit();
                }
              }}
              placeholder="lowercase letters, numbers, dots, underscores (5-42 chars)"
              value={name}
            />
            {nameIsTooShort && (
              <FormControl.ErrorText>
                Name must be at least {NAME_MIN_LENGTH} characters
              </FormControl.ErrorText>
            )}
            {nameHasInvalidChars && (
              <FormControl.ErrorText>
                Name can only contain lowercase letters, numbers, dots, and underscores
              </FormControl.ErrorText>
            )}
          </FormControl>
          <FormControl isInvalid={productIdIsInvalid} isRequired mt={4}>
            <FormControl.Label>Enterprise Architecture Product ID</FormControl.Label>
            <FormControl.HelperText>
              Can be found in AMMA &gt; product entity
            </FormControl.HelperText>
            <Input
              onChange={(e) => setProductId(e.target.value)}
              placeholder="3 to 10 digits (PRO- prefix added automatically)"
              value={productId}
            />
            {productIdIsInvalid && (
              <FormControl.ErrorText>Product ID must contain 3 to 10 digits</FormControl.ErrorText>
            )}
          </FormControl>
          <FormControl isInvalid={emailIsInvalid} isRequired mt={4}>
            <FormControl.Label>Owner Email</FormControl.Label>
            <Input
              maxLength={255}
              onChange={(e) => setOwnerEmail(e.target.value)}
              placeholder="owner@example.com"
              type="email"
              value={ownerEmail}
            />
            {emailIsInvalid && (
              <FormControl.ErrorText>Must be a valid email address</FormControl.ErrorText>
            )}
          </FormControl>
          <FormControl mt={4}>
            <FormControl.Label>Description</FormControl.Label>
            <Textarea
              maxLength={DESCRIPTION_MAX_LENGTH}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
              placeholder="Optional description"
              value={description}
            />
          </FormControl>
          {error && (
            <Text color="error.500" fontSize="sm" mt={4}>
              {error}
            </Text>
          )}
        </Modal.Body>
        <Modal.Footer>
          <HorizontalStack spacing={2}>
            <Button onClick={onClose} variant="outline">
              Cancel
            </Button>
            <Button isDisabled={!canSubmit} isLoading={isSubmitting} onClick={handleSubmit}>
              Create
            </Button>
          </HorizontalStack>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};
